import classNames from "classnames"
import { useNavigate } from "@remix-run/react"
import ResponsiveContentfulImage from "~/components/ResponsiveContentfulImage"
import { useHeaderLocationPicker } from "~/contexts/HeaderLocationPickerContext"
import { isHeroLocationPickerCtaText } from "~/helpers/heroCta"
import { withNonBreakingLocationNames } from "~/helpers/heroHeading"
import ArrowRight from "~/icons/ArrowRight"
import Location from "~/icons/Location"
import type { SerializeFrom } from "@remix-run/node"
import type { WithContentfulHeaderLocationFragment } from "~/contentful"
import { Button } from "../../atoms/Button"
import { StarRating } from "../../atoms"

export interface Props {
  backgroundImageUrl: string
  backgroundImageMobileUrl?: string
  backgroundImageAlt?: string
  heading: string
  subHeading?: string
  ctaText?: string
  ctaLink?: string
  locationPages?: SerializeFrom<WithContentfulHeaderLocationFragment>[]
  className?: string
  reviewText?: string
  overlayImageUrl?: string
  mobileTextAlignment?: "Left" | "Center" | "Right"
  hideOverlay?: boolean
  textColor?: string
  overlayColor?: string
}

const HeroSection = ({
  backgroundImageUrl,
  backgroundImageMobileUrl,
  backgroundImageAlt,
  heading,
  subHeading,
  ctaText,
  ctaLink,
  locationPages = [] as SerializeFrom<WithContentfulHeaderLocationFragment>[],
  className,
  reviewText,
  overlayImageUrl,
  mobileTextAlignment = "Center",
  hideOverlay = false,
  textColor,
  overlayColor
}: Props) => {
  const navigate = useNavigate()
  const headerLocationPicker = useHeaderLocationPicker()

  const pickerCta = isHeroLocationPickerCtaText(ctaText)
  const trimmedCtaText = ctaText?.trim()

  const showLocationIcon =
    pickerCta || (ctaText?.toLowerCase().includes("location") ?? false)

  const hasHeroCtaRow =
    Boolean(trimmedCtaText) && (pickerCta || Boolean(ctaLink?.trim()))

  const handleHeroCtaClick = () => {
    if (!trimmedCtaText) return

    if (pickerCta && locationPages.length > 0) {
      if (!headerLocationPicker) {
        navigate("/visit")
        return
      }

      const didOpen = headerLocationPicker.requestOpenHeaderLocationPicker()
      if (!didOpen) {
        navigate("/visit")
      }
      return
    }

    if (pickerCta && locationPages.length === 0) {
      navigate("/visit")
      return
    }

    if (ctaLink?.trim()) {
      navigate(ctaLink)
    }
  }

  const trailingIcon = showLocationIcon ? (
    <Location className="ml-3" />
  ) : (
    <ArrowRight className="ml-6 h-4 w-4" />
  )

  const ctaLabel = (
    <span className="flex items-center text-lg justify-start sm:justify-between">
      {ctaText} {trailingIcon}
    </span>
  )

  const ctaContainerClass = classNames("mt-6 sm:mt-8 flex sm:justify-center", {
    "justify-start": mobileTextAlignment === "Left",
    "justify-center": mobileTextAlignment === "Center",
    "justify-end": mobileTextAlignment === "Right"
  })

  return (
    <section
      className={classNames(
        "relative flex sm:items-center justify-center min-h-[60vh] w-full overflow-hidden",
        className
      )}
    >
      <div className="absolute inset-0 w-full h-full">
        <ResponsiveContentfulImage
          src={backgroundImageUrl}
          srcMobile={backgroundImageMobileUrl}
          alt={backgroundImageAlt ?? ""}
          aria-hidden={!backgroundImageAlt}
          className="w-full h-full object-cover"
          sizeDesktop="100vw"
          sizeMobile="100vw"
          lazyLoad={false}
        />
      </div>
      {!hideOverlay && (
        <div
          className={classNames("absolute inset-0", {
            "bg-black/40": !overlayColor,
            "opacity-40": overlayColor
          })}
          style={overlayColor ? { backgroundColor: overlayColor } : undefined}
        />
      )}
      <div
        className={classNames(
          "relative z-10 flex flex-col items-center text-center px-7 pt-8 sm:pt-12 pb-12 sm:space-y-2",
          { "justify-between": reviewText, "justify-center": !reviewText }
        )}
      >
        {reviewText && (
          <StarRating
            rating={5}
            text={reviewText}
            textColor="#fff"
            className="flex-col sm:flex-row"
          />
        )}
        <div>
          {overlayImageUrl && (
            <ResponsiveContentfulImage
              src={overlayImageUrl}
              alt=""
              aria-hidden
              className="mx-auto mb-6 h-auto max-w-[400px]"
              sizeDesktop="300px"
              sizeMobile="250px"
            />
          )}
          <h1
            className={classNames(
              "text-5xl font-bold font-serif sm:text-center max-w-[36rem] mx-auto text-pretty",
              {
                "text-left": mobileTextAlignment === "Left",
                "text-center": mobileTextAlignment === "Center",
                "text-right": mobileTextAlignment === "Right"
              }
            )}
            style={{ color: textColor || "white" }}
          >
            {withNonBreakingLocationNames(heading)}
          </h1>
          {subHeading && (
            <p
              className={classNames(
                "text-lg mt-3 sm:text-center text-pretty max-w-[34rem]",
                {
                  "text-left": mobileTextAlignment === "Left",
                  "text-center": mobileTextAlignment === "Center",
                  "text-right": mobileTextAlignment === "Right"
                }
              )}
              style={{ color: textColor || "white" }}
            >
              {subHeading}
            </p>
          )}
          {hasHeroCtaRow && (
            <div className={ctaContainerClass}>
              <Button
                variant="primary"
                style="outlined"
                size="large"
                type="button"
                onClick={handleHeroCtaClick}
              >
                {ctaLabel}
              </Button>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

HeroSection.displayName = "HeroSection"

export { HeroSection }
export default HeroSection
